import * as Flash from 'components/flash'
import * as Loading from 'components/loading'
import { RasaContext } from 'context'
import * as GenericRedux from 'generic/genericRedux'
import { RasaReactComponent } from 'generic/rasaReactComponent'
import React from 'react'
import { Button, Modal, ModalBody, ModalFooter, Row } from 'reactstrap'
import * as Redux from 'redux'
import { injectReducer } from 'store/index'
import { NewsletterContentChat } from './newsletter-content-chat'
import * as Constants from './constants'
import * as BillingPlan from 'shared_server_client/types/billing_plan'
import { ProgressBar } from '@progress/kendo-react-progressbars'
import { UpgradeButton } from '../../upgrade/button'
import { UPGRADE_BUTTON_COLOR } from '../../upgrade/constants'
import * as GA from '../../../google-analytics'
import { DashboardMenuOption } from '../../dashboard-menu/constants'
import { ScoutIcon } from 'components/icons/scout'
import { Dataset } from 'generic/dataset'
import { SourceTypes } from 'shared_server_client/constants'
import { tryParseJSON } from 'generic/utility'
import { HeaderComponent } from 'components/header/component'
import { Grid, GridColumn as Column, GridNoRecords } from '@progress/kendo-react-grid'
import { ActiveCell } from '../kendocells'
import isNil from 'lodash/isNil'
import '../../template-library/_styles.scss'
import '../edit-newsletter-content-source/edit-newsletter-content-source.scss'
import './_styles.scss'

interface AddNewsletterContentState {
  error?: string,
  isLoading: boolean,
  selectedCommunity: string,
  plan?: BillingPlan.BillingPlan
  accountSourcesCount: number
  showChatModal: boolean
  sourceCreated: boolean
  existingSource?: any
  sessionId?: string
  isEditMode: boolean
  filters: any[]
  sourceId?: string
  name: string
  summary: string
  topics: string[]
}

class ContentPoolAddNewsletterContentSourceComponent extends RasaReactComponent<any, AddNewsletterContentState> {
  public static contextType = RasaContext;

  constructor(props: any) {
    super(props, 'communitySource', {
      accountSourcesCount: 0,
      isLoading: true,
      selectedCommunity: '',
      showChatModal: false,
      sourceCreated: false,
      existingSource: null,
      sessionId: undefined,
      isEditMode: false,
      filters: [],
      sourceId: undefined,
      name: '',
      summary: '',
      topics: []
    })
  }

  public componentDidMount = () => {
    // Get ID from URL query params directly (more reliable than Redux store on first load)
    const urlParams = new URLSearchParams(window.location.search)
    const id: string = urlParams.get('id')

    // Initialize user and community info
    this.context.user.init().then(({person, activeCommunity}) => {
      this.selectCommunity(activeCommunity.communityId)
      this.setState({
        accountSourcesCount: activeCommunity.billingInfo.usageStats.sourcesCount,
        selectedCommunity: activeCommunity.communityId,
        plan: activeCommunity.billingInfo.currentPlan
      })

      if (id) {
        // Edit mode - trigger data load (will be handled in componentDidUpdate)
        this.loadOnRouting(id, '/content/add-newsletter-content')
      } else {
        // No ID in URL - check if Newsletter Content source exists
        new Dataset().loadCommunityDataset(
          'communitySources',
          activeCommunity.communityId,
          []
        ).then((response) => {
          const sources = response[0] || []
          const newsletterSource = sources.find(s =>
            s.source_type === SourceTypes.newsletterContent &&
            s.is_archived === 0
          )

          if (newsletterSource) {
            // Source exists - trigger data load (will be handled in componentDidUpdate)
            this.loadOnRouting(newsletterSource.id, '/content/add-newsletter-content')
          } else {
            // Add mode - no existing source
            this.setState({
              isEditMode: false,
              isLoading: false
            })
          }
        }).catch(() => {
          this.setState({ isLoading: false })
        })
      }
    }).catch(() => {
      this.setState({ isLoading: false })
    })
  }

  public componentDidUpdate = (prevProps: any, prevState: AddNewsletterContentState) => {
    // Check if props.data has been loaded and we haven't processed it yet
    if (prevProps.data !== this.props.data) {
      // Try both field names (different entities return different field names)
      const optionsStr = this.props.data.subscription_options || this.props.data.options || this.props.data.subscriptionOptions || '{}'
      const subscriptionOptions = tryParseJSON(optionsStr) || {}

      this.setState({
        existingSource: this.props.data,
        sessionId: subscriptionOptions.session_id,
        isEditMode: true,
        sourceId: this.props.data.id,
        name: this.props.data.name || '',
        summary: subscriptionOptions.summary || '',
        topics: subscriptionOptions.topics || [],
        isLoading: false
      })

      // Load filters for this source
      if (this.state.selectedCommunity) {
        new Dataset().loadCommunityDataset(
          'communitySubscriptionFilters',
          this.state.selectedCommunity,
          [{param: 'sourceId', value: this.props.data.id}]
        ).then((filtersResponse) => {
          this.setState({filters: filtersResponse[0] || []})
        })
      }
    }
  }

  public render = () => {
    if (this.state.sourceCreated) {
      // Show success message and redirect after a delay
      setTimeout(() => {
        this.props.push(DashboardMenuOption.sources)
      }, 2000)

      return (
        <div className="add-topic-wrapper">
          <div className="standard-header-source">
            <div className="titles-and-description-container text-center">
              <h2>Success!</h2>
              <p>Your newsletter content source has been {this.state.isEditMode ? 'updated' : 'created'} successfully.</p>
              <p>Redirecting to your sources...</p>
            </div>
          </div>
        </div>
      )
    }

    if (this.state.isLoading) {
      return <Loading.Loading size={Loading.SIZES.LARGE}/>
    }

    // Add mode - show the old simple UI
    if (!this.state.isEditMode) {
      return (
        this.state.plan ? <div className={`${this.context.store.getState().config.isMobile ? 'add-topic-wrapper-mobile' : 'add-topic-wrapper'}`}>
          <div className="standard-header-source">
            <div className="titles-and-description-container">
              <h2 className="sources-heading">Sources</h2>
              <div className="section-header-text">
                Add a New {Constants.SOURCE_NAME} Source
              </div>
              <p className="section-detail-text">
                Have a conversation with Scout to define your newsletter content. Scout will save the source for you when you're ready.
              </p>

              {!this.maxLimit().maxLimitReached &&
                <div className="topic sources">
                  <div className="image">
                    <ScoutIcon svgwidth={50} svgheight={50}/>
                  </div>
                  <div className="words">
                    <h2>{Constants.SOURCE_NAME}</h2>
                    <p>Chat with Scout to define and save your newsletter content</p>
                    <div className="input-area">
                      <Button onClick={() => this.setState({ showChatModal: true })}>
                        Add Newsletter Content
                      </Button>
                    </div>
                    {this.state.error &&
                      <span className="warning">{this.state.error}</span>
                    }
                  </div>
                </div>
              }
            </div>
            <this.limitReached/>
          </div>

          {this.renderChatModal()}
        </div> : null)
    }

    // Edit mode - show the better UI from edit page
    return (
      <div className="edit-newsletter-content-source">
        <Row>
          <HeaderComponent
            title={'SOURCES'}
            subTitle={'Edit Newsletter Content Source'}
          />
        </Row>
        <div className="newsletter-content-container">
          <Row>
            <div className="source-info-card">
              <div className="source-header">
                <div className="source-type-badge">
                  <ScoutIcon svgwidth={24} svgheight={24}/>
                  <strong>Newsletter Content</strong>
                </div>
                <Button
                  color="primary"
                  size="sm"
                  onClick={() => this.setState({ showChatModal: true })}
                  className="edit-with-scout-btn"
                >
                  <ScoutIcon svgwidth={16} svgheight={16}/>
                  <span style={{ marginLeft: '8px' }}>Edit with Scout</span>
                </Button>
              </div>

              <div className="source-content">
                <div className="field-group">
                  <label className="field-label">Source Name</label>
                  <div className="field-value name-field">{this.state.name || 'No name'}</div>
                </div>

                <div className="field-group">
                  <label className="field-label">Summary</label>
                  <div className="field-value summary-field">
                    {this.state.summary || '(No summary available - click "Edit with Scout" to add one)'}
                  </div>
                </div>

                <div className="field-group">
                  <label className="field-label">Topics ({this.state.topics.length})</label>
                  <div className="topics-container">
                    {this.state.topics && this.state.topics.length > 0 ? (
                      this.state.topics.map((topic, index) => (
                        <div key={index} className="topic-chip">
                          {topic}
                        </div>
                      ))
                    ) : (
                      <div style={{ color: '#666', fontSize: '14px', fontStyle: 'italic' }}>
                        No topics defined - click "Edit with Scout" to add topics
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </Row>

          <Row>
            <div className="filters-section">
              <h5>Exclude Filters for This Source</h5>
              <div className="filters-description">
                <p>Activate or deactivate your exclude filters for this source.</p>
                <p>
                  <strong>No</strong> articles will come in that match one or more of these topics.
                </p>
              </div>

              <div className="filters-grid rasa-data-grid">
                <Grid data={this.state.filters.filter((e) => e.type === 'blacklist')}
                      editField="is_active">
                  <Column field="is_active" title="Status" cell={this.FilterActiveCell} width="85" />
                  <Column field="name" title="Topic, Keyword, or Phrase" editable={false} />
                  <GridNoRecords><a href="/content/filters">Add a filter</a></GridNoRecords>
                </Grid>
              </div>
            </div>
          </Row>
        </div>
        {this.renderChatModal()}
      </div>
    )
  }

  protected limitReached = () => {
    const {maxSourceCount, isUnlimited, maxLimitReached} = this.maxLimit()
    return <div className="design-right-side">
      <div className="topic-source-right-side">
        {!isUnlimited ?
          <div className="div-progress-bar">
            <ProgressBar
              min={0}
              className={`progress-bar${maxLimitReached ? '-red' : ''}`}
              max={maxSourceCount}
              value={this.state.accountSourcesCount}
              labelVisible={false}/>
            <div className="progress-bar-label">
              <div>Sources used</div>
              <div>{this.state.accountSourcesCount}/{maxSourceCount}</div>
            </div>
          </div> : null}
        {maxLimitReached &&
          <UpgradeButton color={UPGRADE_BUTTON_COLOR.WHITE} showIcon={true}
                       source={GA.UpgradeSource.AddSource} text="Upgrade"/>
        }
      </div>
    </div>
  }

  private renderChatModal = () => {
    return (
      <div className="rex-modal-container">
        <Modal size="xl" isOpen={this.state.showChatModal}>
          <ModalBody>
            <NewsletterContentChat
              communityId={this.state.selectedCommunity}
              onError={this.handleChatError}
              onSourceCreated={this.handleSourceCreated}
              sessionId={this.state.sessionId}
              isEditMode={this.state.isEditMode}
            />
          </ModalBody>
          <ModalFooter>
            <Button onClick={this.closeChatModal}>
              Close
            </Button>
          </ModalFooter>
        </Modal>
      </div>
    )
  }

  private FilterActiveCell = (props: any) => {
    return <ActiveCell {...props} onChange={this.toggleFilter} />
  }

  private toggleFilter = (e: any) => {
    return this.context.entityMetadata.getEntityObject('community_filtered_subscription')
              .then((entity: any) => {
                entity.setRecordId(this.state.selectedCommunity, e.dataItem.id)
                entity.data.isActive = !e.dataItem.is_active
                if (isNil(e.dataItem.id)) {
                  entity.data.subscriptionId = this.state.sourceId
                  entity.data.filterGroupId = e.dataItem.filter_group_id
                  entity.data.filterType = e.dataItem.type
                }
                return entity.save().then(() => {
                  const filters = this.state.filters
                  filters[this.state.filters
                          .findIndex((record) => record === e.dataItem)].is_active = !e.dataItem.is_active
                  this.setState({filters})
                })
              })
  }

  private handleSourceCreated = () => {
    const message = this.state.isEditMode
      ? 'Newsletter content source updated successfully!'
      : 'Newsletter content source created successfully!'
    this.context.store.dispatch(Flash.showFlashMessage(message))

    if (this.state.isEditMode) {
      // In edit mode, reload the page to show updated data
      this.setState({ showChatModal: false })
      window.location.reload()
    } else {
      // In add mode, show success message and redirect
      this.setState({
        sourceCreated: true,
        showChatModal: false
      })
    }
  }

  private handleChatError = (error: string) => {
    this.context.store.dispatch(Flash.showFlashError(error))
  }

  private closeChatModal = () => {
    this.setState({ showChatModal: false })
  }

  private maxLimit = () => {
    const maxSourceCount = BillingPlan.getCurrentPlanMaxSources(this.state.plan)
    const isUnlimited = maxSourceCount === BillingPlan.UNLIMITED_VALUE || maxSourceCount === -1
    const maxLimitReached = !isUnlimited &&
      this.state.accountSourcesCount >= maxSourceCount
    return {
      maxSourceCount,
      isUnlimited,
      maxLimitReached
    }
  }

}

export const ContentPoolAddNewsletterContentSource = GenericRedux.createConnect(
  ContentPoolAddNewsletterContentSourceComponent,
  Constants.REDUX_STORE_HOME,
)
injectReducer(
  Constants.REDUX_STORE_HOME,
  Redux.combineReducers({
    data: GenericRedux.createGenericReducer(
      Constants.REDUX_STORE_HOME,
      {url: ''},
    ),
  }),
)
